import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Home as HomeIcon, TrendingUp, Bed, ArrowRight, ExternalLink, Search } from "lucide-react";
import { api } from "../lib/api";
import { cityFromSlug, HAR_QUICK_CHIPS, citySlug } from "../lib/harMarketAreas";
import Seo from "../components/Seo";
import SaveHeart from "../components/SaveHeart";
import IdxDisclaimer from "../components/IdxDisclaimer";
import JsonLd from "../components/JsonLd";
import HoverCyclePhoto from "../components/HoverCyclePhoto";

const fmt = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");
const fmtShort = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
};

export default function HomesForSale() {
  const { citySlug: slug } = useParams();
  const navigate = useNavigate();
  const city = useMemo(() => cityFromSlug(slug), [slug]);

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!city) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      api.get(`/mls/market-stats/${encodeURIComponent(city)}`),
      api.get("/mls/listings", { params: { city, status: "Active", limit: 12 } }),
    ])
      .then(([s, l]) => {
        setStats(s.data);
        setItems(l.data?.items || []);
      })
      .catch(() => {
        setStats(null);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [city]);

  if (!city) {
    return (
      <div className="max-w-lg mx-auto mt-24 p-10 bg-white border border-gray-200 rounded-xl text-center">
        <div className="font-display text-2xl mb-2">Market area not found</div>
        <Link to="/properties" className="text-[#E63946] hover:underline text-sm">
          ← Browse all Houston-metro homes
        </Link>
      </div>
    );
  }

  const relatedChips = HAR_QUICK_CHIPS.filter((c) => c !== city).slice(0, 8);

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="homes-for-sale-page">
      <Seo
        title={`${city}, TX Homes for Sale · Live HAR MLS`}
        description={`${stats?.count || 0} active listings in ${city}, TX with median price ${fmt(stats?.median_price)}. Live HAR MLS inventory, refreshed nightly by Gens Realty.`}
        path={`/homes-for-sale/${slug}`}
      />
      <JsonLd
        id={`ld-${slug}`}
        data={
          stats && stats.count > 0
            ? {
                "@context": "https://schema.org",
                "@type": "Place",
                name: `${city}, TX Homes for Sale`,
                description: `${stats.count} active homes for sale in ${city}, TX. Median list price ${fmt(stats.median_price)}. Live HAR MLS inventory delivered by Gens Realty.`,
                address: {
                  "@type": "PostalAddress",
                  addressLocality: city,
                  addressRegion: "TX",
                  addressCountry: "US",
                },
                url: `https://gensrealty.com/homes-for-sale/${slug}`,
                additionalProperty: [
                  { "@type": "PropertyValue", name: "Active Listings", value: stats.count },
                  { "@type": "PropertyValue", name: "Median List Price (USD)", value: stats.median_price },
                  { "@type": "PropertyValue", name: "Average Bedrooms", value: stats.avg_beds },
                ],
                aggregateRating: undefined,
                ...(items.length > 0 && {
                  makesOffer: items.slice(0, 5).map((l) => ({
                    "@type": "Offer",
                    price: l.price,
                    priceCurrency: "USD",
                    itemOffered: {
                      "@type": "SingleFamilyResidence",
                      name: l.address,
                      address: {
                        "@type": "PostalAddress",
                        streetAddress: l.address,
                        addressLocality: l.city,
                        addressRegion: l.state || "TX",
                        postalCode: l.zip,
                        addressCountry: "US",
                      },
                      numberOfRooms: l.beds,
                      floorSize: l.sqft ? { "@type": "QuantitativeValue", value: l.sqft, unitCode: "FTK" } : undefined,
                      image: l.photos?.[0],
                      url: `https://gensrealty.com/mls-listings/${l.listing_key}`,
                    },
                  })),
                }),
              }
            : null
        }
      />

      {/* HERO */}
      <section className="bg-gradient-to-b from-[#0A0A0A] to-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="flex items-center gap-3 mb-4 text-white/70">
            <div className="w-8 h-px bg-[#C9A961]" />
            <div className="eyebrow flex items-center gap-2">
              <MapPin className="w-3 h-3 text-[#E63946]" />
              Market Area · Greater Houston
            </div>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight font-light">
            {city}, TX <br />
            <span className="serif-italic text-[#E63946]">homes for sale.</span>
          </h1>
          <p className="text-white/75 mt-6 max-w-2xl text-lg font-light">
            Live HAR MLS inventory for {city} — updated nightly. Save homes, tour with a local Gens Realty agent, and get pre-approved through Edge Home Finance in one place.
          </p>

          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10">
            <StatCard
              icon={<HomeIcon className="w-4 h-4" strokeWidth={1.5} />}
              label="Active listings"
              value={loading ? "…" : (stats?.count ?? 0).toLocaleString()}
              testId="stat-count"
            />
            <StatCard
              icon={<TrendingUp className="w-4 h-4" strokeWidth={1.5} />}
              label="Median price"
              value={loading ? "…" : fmtShort(stats?.median_price)}
              testId="stat-median"
            />
            <StatCard
              icon={<Bed className="w-4 h-4" strokeWidth={1.5} />}
              label="Avg beds"
              value={loading ? "…" : (stats?.avg_beds ?? "—")}
              testId="stat-beds"
            />
            <StatCard
              icon={<Search className="w-4 h-4" strokeWidth={1.5} />}
              label="Price range"
              value={
                loading
                  ? "…"
                  : stats?.min_price
                  ? `${fmtShort(stats.min_price)} – ${fmtShort(stats.max_price)}`
                  : "—"
              }
              testId="stat-range"
            />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={`/properties?city=${encodeURIComponent(city)}`}
              className="bg-[#E63946] hover:bg-[#c9313d] text-white px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2"
              data-testid="hfs-see-all"
            >
              See all {stats?.count ?? ""} listings <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/mls-map"
              className="border border-white/30 hover:border-[#E63946] hover:text-[#E63946] text-white px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2"
              data-testid="hfs-map"
            >
              View on map <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Sample inventory */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#C9A961]" />
              <div className="eyebrow text-[#0A0A0A]">Now available in {city}</div>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[0.95] font-light">
              Homes currently <span className="serif-italic text-[#E63946]">on the market.</span>
            </h2>
          </div>
          <Link
            to={`/properties?city=${encodeURIComponent(city)}`}
            className="text-xs font-medium uppercase tracking-[0.25em] text-[#0A0A0A] editorial-link"
            data-testid="hfs-viewall-listings"
          >
            View all {city} listings
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-1/3" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-14 text-center">
            <div className="font-display text-2xl">No active listings in {city} right now.</div>
            <p className="text-[#555] mt-3">
              New inventory hits the HAR MLS every day. Save your search or explore nearby areas below.
            </p>
            <Link
              to="/properties"
              className="mt-6 inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#c9313d] text-white px-6 py-3 rounded-md text-sm font-semibold"
              data-testid="hfs-empty-browse"
            >
              Browse all Houston-metro homes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((l) => (
              <Link
                key={l.listing_key}
                to={`/mls-listings/${l.listing_key}`}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group"
                data-testid={`hfs-card-${l.listing_key}`}
              >
                <div className="relative aspect-[4/3] bg-stone-100">
                  <HoverCyclePhoto photos={l.photos || []} alt={l.address} className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded text-[10px] uppercase tracking-[0.15em] font-semibold">
                    {l.status}
                  </div>
                  <SaveHeart
                    listingKey={l.listing_key}
                    address={l.address}
                    className="absolute top-3 right-3"
                  />
                </div>
                <div className="p-5">
                  <div className="font-display text-2xl font-semibold">{fmt(l.price)}</div>
                  <div className="text-sm text-[#1A1A1A] mt-1">{l.address}</div>
                  <div className="text-xs text-gray-500">{l.city}, {l.state} {l.zip || ""}</div>
                  <div className="text-xs text-gray-600 mt-2">
                    {l.beds} bd · {l.baths} ba · {l.sqft ? Number(l.sqft).toLocaleString() : "—"} sqft
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Related market areas */}
      <section className="bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-4">Explore nearby</div>
          <div className="flex flex-wrap gap-2">
            {relatedChips.map((c) => (
              <Link
                key={c}
                to={`/homes-for-sale/${citySlug(c)}`}
                className="text-xs bg-stone-50 hover:bg-[#E63946] hover:text-white text-[#1A1A1A] px-3 py-1.5 rounded-full border border-stone-200 transition-colors"
                data-testid={`hfs-related-${citySlug(c)}`}
              >
                {c}, TX
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <IdxDisclaimer />
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/60">
        {icon} {label}
      </div>
      <div className="font-display text-2xl lg:text-3xl mt-1 text-white">{value}</div>
    </div>
  );
}
